import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { X } from 'lucide-react';
import MorphButton from './MorphButton';
import './Auth.css';
import './AuthModal.css';

const AuthModal = ({ open, onOpenChange, defaultMode = 'signin' }) => {
  const [isSignUp, setIsSignUp] = useState(defaultMode === 'signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState('player');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  
  // Sync isSignUp with defaultMode when it changes
  React.useEffect(() => {
    setIsSignUp(defaultMode === 'signup');
  }, [defaultMode]);

  const { signUp, signIn, resetPassword } = useAuth();

  // Debug logging
  React.useEffect(() => {
    console.log('AuthModal - open prop changed:', open);
    console.log('AuthModal - defaultMode:', defaultMode);
    console.log('AuthModal - isSignUp:', isSignUp);
    
    if (open) {
      // Check if Dialog is in DOM - Portal renders to document.body
      // Try multiple times with increasing delays
      const checkDialog = (attempt = 1) => {
        const dialogContent = document.querySelector('[data-radix-dialog-content]');
        const dialogOverlay = document.querySelector('[data-radix-dialog-overlay]');
        const allRadixElements = document.querySelectorAll('[data-radix-dialog-content], [data-radix-dialog-overlay]');
        
        console.log(`Dialog check attempt ${attempt}:`);
        console.log('Dialog Content in DOM:', !!dialogContent);
        console.log('Dialog Overlay in DOM:', !!dialogOverlay);
        console.log('All Radix Dialog elements found:', allRadixElements.length);
        console.log('Body children count:', document.body.children.length);
        
        if (dialogContent) {
          const styles = window.getComputedStyle(dialogContent);
          console.log('Dialog Content styles:', {
            display: styles.display,
            opacity: styles.opacity,
            visibility: styles.visibility,
            zIndex: styles.zIndex,
            position: styles.position,
            transform: styles.transform
          });
          console.log('Dialog Content data-state:', dialogContent.getAttribute('data-state'));
          console.log('Dialog Content classes:', dialogContent.className);
        } else if (attempt < 5) {
          // Retry if not found
          setTimeout(() => checkDialog(attempt + 1), 100 * attempt);
        } else {
          console.error('Dialog Content NOT found in DOM after multiple attempts!');
          console.log('Checking if Dialog component rendered at all...');
        }
      };
      
      setTimeout(() => checkDialog(), 50);
    }
  }, [open, defaultMode, isSignUp]);

  // Reset form when modal opens/closes
  React.useEffect(() => {
    if (!open) {
      setEmail('');
      setPassword('');
      setFirstName('');
      setLastName('');
      setRole('player');
      setError('');
      setMessage('');
      setIsSignUp(defaultMode === 'signup');
      setShowResetPassword(false);
    }
  }, [open, defaultMode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      if (isSignUp) {
        const fullName = `${firstName} ${lastName}`.trim();
        const { error, warning, needsEmailConfirmation } = await signUp(email, password, fullName, role);
        if (error) {
          setError(error.message || 'Failed to sign up');
        } else if (warning) {
          setMessage(warning);
          // Reset form
          setEmail('');
          setPassword('');
          setFirstName('');
          setLastName('');
        } else if (needsEmailConfirmation) {
          setMessage('Check your email to confirm your account!');
          // Reset form
          setEmail('');
          setPassword('');
          setFirstName('');
          setLastName('');
        } else {
          // User is signed in automatically (email confirmations disabled)
          setMessage('Account created successfully! Redirecting...');
          // Close modal after a brief delay
          setTimeout(() => {
            onOpenChange(false);
          }, 1000);
        }
      } else {
        const { error } = await signIn(email, password);
        if (error) {
          setError(error.message || 'Failed to sign in');
        } else {
          // Close modal on successful sign in
          onOpenChange(false);
        }
      }
    } catch (err) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Always render Dialog, Radix UI controls visibility
  console.log('AuthModal render - open:', open, 'defaultMode:', defaultMode);
  
  // Force render check
  React.useEffect(() => {
    if (open) {
      console.log('Dialog should be open, checking DOM in 500ms...');
      setTimeout(() => {
        const allDialogs = document.querySelectorAll('[data-radix-dialog-content], [data-radix-dialog-overlay], [role="dialog"]');
        console.log('All dialog-related elements:', allDialogs.length);
        allDialogs.forEach((el, i) => {
          console.log(`Dialog element ${i}:`, el.tagName, el.className, el.getAttribute('data-state'));
        });
      }, 500);
    }
  }, [open]);
  
  // Log render to verify component is rendering
  console.log('AuthModal rendering with open:', open);
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
          className="auth-modal-content" 
          style={{
            maxWidth: '500px',
            width: '90%',
            padding: '2rem',
            background: '#1a1a1a',
            backgroundColor: '#1a1a1a',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            color: '#ffffff',
            opacity: 1,
            visibility: 'visible',
            display: 'grid',
            position: 'fixed',
            zIndex: 10001,
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)'
          }}
        >
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            width: '2rem',
            height: '2rem',
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '6px',
            color: '#ffffff',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.1)';
            e.target.style.borderColor = 'rgba(255, 255, 255, 0.5)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'transparent';
            e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
          }}
          aria-label="Close"
        >
          <X size={18} />
        </button>

        <DialogHeader>
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <img 
              src="/rollerCoaster_logo.svg" 
              alt="The Creative Coaster Logo" 
              style={{ width: '60px', height: '60px', margin: '0 auto 1rem' }}
            />
            <DialogTitle style={{ 
              color: '#ffffff', 
              fontSize: '1.5rem', 
              fontWeight: 700,
              marginBottom: '0.5rem'
            }}>
              The Creative Coaster
            </DialogTitle>
            <DialogDescription style={{ 
              color: 'rgba(255, 255, 255, 0.7)', 
              fontSize: '0.875rem' 
            }}>
              {isSignUp ? 'Create your account' : 'Sign in to your account'}
            </DialogDescription>
          </div>
        </DialogHeader>

        {showResetPassword ? (
          // Password Reset View
          <div className="auth-form">
            <div className="auth-field">
              <Label htmlFor="resetEmail">Enter your email to reset password</Label>
              <Input
                id="resetEmail"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
              />
            </div>

            {error && (
              <div className="auth-error">
                {error}
              </div>
            )}

            {message && (
              <div className="auth-message">
                {message}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', alignItems: 'stretch' }}>
              <MorphButton
                type="button"
                loading={loading}
                onClick={async () => {
                  if (!email.trim()) {
                    setError('Please enter your email');
                    return;
                  }
                  setError('');
                  setMessage('');
                  setLoading(true);
                  if (!process.env.REACT_APP_SUPABASE_URL || !process.env.REACT_APP_SUPABASE_ANON_KEY) {
                    setError('Local Supabase credentials are missing. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to a .env file in the project root, then restart the dev server.');
                    setLoading(false);
                    return;
                  }
                  const { error: resetError } = await resetPassword(email);
                  if (resetError) {
                    console.error('Password reset error details:', resetError);
                    let errorMessage = resetError.message || 'Failed to send reset email';

                    if (errorMessage === 'Failed to fetch') {
                      errorMessage = 'Could not reach Supabase (Failed to fetch). Check that .env has the correct REACT_APP_SUPABASE_URL / REACT_APP_SUPABASE_ANON_KEY and that you restarted npm start after saving .env.';
                    }
                    
                    if (resetError.message?.toLowerCase().includes('email') || 
                        resetError.message?.toLowerCase().includes('smtp') ||
                        resetError.message?.toLowerCase().includes('configuration')) {
                      errorMessage = `Email service error: ${resetError.message}. Please verify SMTP is enabled and configured correctly in Supabase.`;
                    }
                    
                    setError(errorMessage);
                  } else {
                    setMessage('Password reset email sent! Check your inbox and follow the link to set a new password.');
                    setEmail('');
                  }
                  setLoading(false);
                }}
                style={{ flex: 1, marginTop: 0 }}
              >
                Send Reset Email
              </MorphButton>
              <button
                type="button"
                onClick={() => {
                  setShowResetPassword(false);
                  setError('');
                  setMessage('');
                  setEmail('');
                }}
                style={{
                  flex: 1,
                  height: 45,
                  padding: '0.875rem 1.25rem',
                  borderRadius: 8,
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  background: 'rgba(255, 255, 255, 0.06)',
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxSizing: 'border-box',
                  transition: 'background 0.2s ease, border-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.35)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.06)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          // Sign In / Sign Up View
          <form onSubmit={handleSubmit} className="auth-form">
            {isSignUp && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="auth-field">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First name"
                    required
                  />
                </div>
                <div className="auth-field">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Last name"
                    required
                  />
                </div>
              </div>
            )}

            <div className="auth-field">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
              />
            </div>

            <div className="auth-field">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                minLength={6}
              />
              {!isSignUp && (
                <button
                  type="button"
                  onClick={() => {
                    setShowResetPassword(true);
                    setError('');
                    setMessage('');
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#D702F6',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    marginTop: '0.5rem',
                    textDecoration: 'underline',
                    padding: 0,
                    textAlign: 'left'
                  }}
                >
                  Forgot Password?
                </button>
              )}
            </div>

            {/* Host/Player selector for sign up only */}
            {isSignUp && (
              <div className="auth-role-block">
                <Label className="auth-role-label">Choose account type</Label>
                <div className="auth-role-options">
                  <label className="auth-role-option">
                    <input
                      type="radio"
                      name="role"
                      value="player"
                      checked={role === 'player'}
                      onChange={() => setRole('player')}
                    />
                    <span className="auth-role-description">Player – play and keep track of your stats</span>
                  </label>
                  <label className="auth-role-option">
                    <input
                      type="radio"
                      name="role"
                      value="host"
                      checked={role === 'host'}
                      onChange={() => setRole('host')}
                    />
                    <span className="auth-role-description">Host – create and host games</span>
                  </label>
                </div>
              </div>
            )}

            {error && (
              <div className="auth-error">
                {error}
              </div>
            )}

            {message && (
              <div className="auth-message">
                {message}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="auth-submit-button"
              style={{
                width: '100%',
                marginTop: '1rem'
              }}
            >
              {loading ? 'Please wait...' : isSignUp ? 'Sign Up' : 'Sign In'}
            </Button>
          </form>
        )}

        <div className="auth-footer" style={{ marginTop: '1.5rem', textAlign: 'center' }}>
          <button
            type="button"
            onClick={() => {
              setIsSignUp(!isSignUp);
              setError('');
              setMessage('');
            }}
            className="auth-toggle"
            style={{
              background: 'none',
              border: 'none',
              color: 'rgba(255, 255, 255, 0.7)',
              cursor: 'pointer',
              fontSize: '0.875rem',
              padding: '0.5rem'
            }}
          >
            {isSignUp ? (
              <>Already have an account? <span style={{ color: '#D702F6', fontWeight: 600 }}>Sign In</span></>
            ) : (
              <>Don't have an account? <span style={{ color: '#D702F6', fontWeight: 600 }}>Sign Up</span></>
            )}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;

